import { Component, EventEmitter, OnInit, Renderer2, ChangeDetectionStrategy } from '@angular/core';

import * as L from 'leaflet';
import { Map } from 'leaflet';
import 'iso8601-js-period';
import 'leaflet.markercluster';
import 'leaflet-timedimension';
import { FullScreen } from 'leaflet.fullscreen';
import { VesselsService, Voyage } from '../vessels.service';
import moment from 'moment';
import { MessageLevel, PgMessagesService } from '../../../shared/services/pg-messages.service';
import { forkJoin } from 'rxjs';
import {
  NoonReportsService,
  NoonReportV202005,
  SelectedLocation,
} from '../services/noon-reports.service';
import { FleetStatusToolbarComponent } from './fleet-status-toolbar/fleet-status-toolbar.component';
import { OrganisationService } from 'src/app/organisation/organisation.service';
import { Organisation } from 'src/app/generated/services/organisations/organisation';
import { DateRange } from '../../../shared/components/date-range.component';
import {
  PgDateRangeTitleComponent,
  PgDateRangeTitleEnum, PgTitleComponent,
  PgToolbarComponent
} from '../../../shared/components/title-bar/title-bar.component';
import { DateTimeHelpers } from 'src/app/shared/helpers/date-time.helpers';
import { SharedModule } from 'src/app/shared/shared.module';
import { AuthenticationModule } from 'src/app/authentication/authentication.module';
import { AgGridModule } from 'ag-grid-angular';
import { GridOptions, GridReadyEvent } from 'ag-grid-community';
import { AgGridService } from 'src/app/shared/ag-grid/ag-grid.service';
import { Router } from '@angular/router';

@Component({
  selector: 'app-fleet-status',
  templateUrl: './fleet-status.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [SharedModule, AgGridModule, AuthenticationModule]
})
export class FleetStatusComponent implements OnInit, PgTitleComponent, PgDateRangeTitleComponent, PgToolbarComponent {

  title = new EventEmitter<string>();
  dateRangeSubTitle = new EventEmitter<[DateRange, PgDateRangeTitleEnum]>();
  toolbarComponentType = FleetStatusToolbarComponent;
  toolbarComponent!: FleetStatusToolbarComponent;
  _map!: Map;
  ongoingVoyages!: Voyage[];
  locations!: SelectedLocation[];
  arrivalDepartures!: NoonReportV202005[];

  ports!: Organisation[];
  offices!: Organisation[];
  airports!: Organisation[];

  _toolbar: any;
  _currentVesselsLayer: any;

  gridOptions: GridOptions = {};

  constructor(private _vesselsService: VesselsService,
    private _noonReportV202005sService: NoonReportsService,
    private _pgMessagesService: PgMessagesService,
    private _organisationService: OrganisationService,
    private _router: Router,
    private _renderer: Renderer2) { }

  navigateVessel(_router: Router, data: any) {
    _router.navigate(['/vessels/vessel', data.vesselImo]);
  }
  navigateVoyage(_router: Router, data: any) {
    _router.navigate(['/vessels/voyage', data.id]);
  }

  columnDefs = [
    {
      headerName: 'Vessel',
      field: 'vesselName',
      valueFormatter: (row:any) => row.value ? row.value : 'Edit',
      cellRenderer: (row:any) => {
        const text = row.value;
        return AgGridService.editButtonWithText(
          this._renderer,
          this._router,
          this.navigateVessel,
          row.data,
          text
        );
      }
    },

    {
      headerName: 'Ship Voyage No.',
      field: 'shipVoyageNo',
      valueFormatter: (row:any) => row.value ? row.value : 'Edit',
      cellRenderer: (row:any) => {
        const text = row.value;
        return AgGridService.editButtonWithText(
          this._renderer,
          this._router,
          this.navigateVoyage,
          row.data,
          text
        );
      }
    },

    {
      headerName: 'Agent Voyage No.', field: 'agentVoyageNo',
      cellRenderer: (row:any) => {
        const voyage = (row.data.agentVoyageNo) ? row.data.agentVoyageNo : 'Edit';
        return AgGridService.editButtonWithText(this._renderer, this._router, this.navigateVoyage, row.data, voyage);
      }
    },
    {
      headerName: 'Days',
      cellRenderer: (row:any) => VesselsService.voyageLengthFromLoadingToDischarge(row.data)
    },
  ];

  arrivalsColumnDefs = [
    { headerName: 'Vessel', field: 'vesselName' },
    { headerName: 'Voyage', field: 'voyageDetails.voyageNumber' },
    { headerName: 'Type', field: 'reportType', },
    {
      headerName: 'Date(UTC)',
      field: 'date',
      valueFormatter: (row:any) => DateTimeHelpers.formatUTCDateTime(row.value.timestamp)
    },
    { headerName: 'Source', field: 'voyageDetails.source' },
    { headerName: 'Destination', field: 'voyageDetails.destination' },
  ];

  ngOnInit() {
    this.gridOptions = AgGridService.getOptions();
    this._organisationService.get().subscribe(organisations => {
      this.ports = organisations.filter(o => (o.location!.latitude !== 0 && o.location!.longitude !== 0)
        && (o.properties['isAnchorage'] === true || o.properties['isPort'] === true));
      this.offices = organisations.filter(o => (o.location!.latitude !== 0 && o.location!.longitude !== 0)
        && (o.properties['office'] === true));
      this.airports = organisations.filter(o => (o.location!.latitude !== 0 && o.location!.longitude !== 0)
        && (o.properties['airport'] === true));
      this.setupMap();
      this.refreshData();
    });
  }

  gridSetting(event: GridReadyEvent) {
    AgGridService.setting(event);
  }

  refreshData() {
    const dr = {
      startDate: moment().startOf('day').add(-200, 'days').toDate(),
      endDate: moment().endOf('day').toDate()
    };
    forkJoin([
      this._vesselsService.getLastActiveVesselVoyages(),
      this._noonReportV202005sService.getVesselLatestReportV2020(),
      this._noonReportV202005sService.getNoonReportsV202005Between(dr)
    ]).subscribe(results => {
      this.ongoingVoyages = results[0];
      this.locations = this._noonReportV202005sService.groupLocations(results[1])
        .sort((a, b) => a.vesselName.localeCompare(b.vesselName));
      this.title.emit('Fleet status');
      this.dateRangeSubTitle.emit([dr, PgDateRangeTitleEnum.defaultTitle]);
      this.toolbarComponent.locations = this.locations;
      this.toolbarComponent.itemsSelected.subscribe(l => this.onItemSelected(l));
      this.arrivalDepartures = results[2]
        .filter(report => report.reportType !== 'Noon')
        .sort((a, b) =>
          a.date!.timestamp - b.date!.timestamp);
      if (this.locations.length === 0) {
        this._pgMessagesService.publishMessage({
          message: 'Noon reports are missing',
          topic: 'Noon reports',
          level: MessageLevel.Warning
        });
      } else {
        this.drawLatestLocations(this.locations);
      }
    });
  }

  setupMap() {
    if (!this._map) {
      const baseLayer = L.tileLayer('https://{s}.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}{r}.png?key=cb1_3058_1_ec4dda93438a946bbbda70da', {
        attribution: `&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> &copy;
        <a href="https://carto.com/attributions">CARTO</a>`
      });

      const mapOptions = {
        zoomControl: false,
        zoom: 14,
        layers: [baseLayer]
      };

      this._map = L.map('mapid', mapOptions);
      this._map.scrollWheelZoom.disable();

      const baseMaps = {
        basemap: baseLayer
      };

      const layers = {
        ports: L.layerGroup(this.getLocationMarketLayer(this.ports, 'fa fa-anchor', '')),
        offices: L.layerGroup(this.getLocationMarketLayer(this.offices, 'fa fa-building', 'gray')),
        airports: L.layerGroup(this.getLocationMarketLayer(this.airports, 'fas fa-plane-departure', 'text-dark-yellow'))
      };

      L.control.layers(baseMaps, layers, { position: 'topleft' }).addTo(this._map);
      L.control.zoom({ position: 'topleft' }).addTo(this._map);
      this._map.addControl(new FullScreen());

      // const component = this;
      // const refreshAction = L.Toolbar2.Action.extend({
      //   options: {
      //     toolbarIcon: {
      //       html: '<i class="fa fa-window-maximize"></i>',
      //       tooltip: 'Reset bounds'
      //     }
      //   },
      //   addHooks: () => {
      //     const bounds = component._noonReportV202005sService.getLocationBounds(component.locations);
      //     component._map.fitBounds(bounds);
      //   }
      // });

      // const showPopupsAction = L2.Toolbar2.Action.extend({
      //   options: {
      //     toolbarIcon: {
      //       html: '<i class="fa fa-comment"></i>',
      //       tooltip: 'Show details'
      //     }
      //   },
      //   addHooks: function () { component.showPopups(); }
      // });

      // L.Toolbar2.Action.extend({
      //   initialize(map, myAction) {
      //     this.map = map;
      //     this.myAction = myAction;
      //     L.Toolbar2.Action.prototype.initialize.call(this);
      //   },
      //   addHooks() {
      //     this.myAction.disable();
      //   }
      // });

      // this._toolbar = new L.Toolbar2.Control({
      //   position: 'topleft',
      //   // actions: [refreshAction, showPopupsAction]
      //   // actions: [refreshAction]
      // }).addTo(this._map);
    }
  }

  getLocationMarketLayer(location: Organisation[], icon: string, colour: string) {
    return location.map(p => {
      const markerOptions: L.MarkerOptions = {
        icon: L.divIcon({
          html: `<i class="${icon}  ${colour}" style="font-size:15px; opacity:0.9"></i>
          <br><i style="bg-secondary"></i>`,
          iconSize: [30, 30],
          className: 'portDivIcon'
        }),
      };
      return L.marker([p.location!.latitude, p.location!.longitude], markerOptions)
        .bindPopup(`${p.nameTranslations['en']}`, { maxWidth: 500 });
    });
  }

  onItemSelected(locations: SelectedLocation[]) {
    this.drawLatestLocations(locations.filter(r => r.selected));
  }

  drawLatestLocations(locations: SelectedLocation[]) {
    const bounds = this._noonReportV202005sService.getLocationBounds(locations);
    const vesselsLayer = this.getVesselsLatestLocationLayer(locations);

    if (this._currentVesselsLayer) {
      this._map.removeLayer(this._currentVesselsLayer);
    }

    this._currentVesselsLayer = vesselsLayer;

    this._map.fitBounds(bounds);
    this._map.addLayer(vesselsLayer);
  }

  private getVesselsLatestLocationLayer(reports: SelectedLocation[]) {
    const pointGeoJson = reports.map(r => (
      {
        type: 'Feature',
        properties: {
          name: r.vesselName,
          locations: r.locations,
          times: r.locations.map(n => n.date)
        },
        geometry: {
          type: 'Point',
          coordinates: r.locations.map(n =>
            [n.position?.geoCoordinate!.longitude, n.position?.geoCoordinate!.latitude])[0]
        }
      }
    ));

    return L.geoJSON(pointGeoJson as any, {
      pointToLayer: (feature:any) => {
        return this._noonReportV202005sService.getVesselLocationMarkerLayer(feature.properties.locations[0]);
      },
      filter: () => true
    });
  }

}
